import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, MapPin, Eye, Calendar, Search, Download, Shield, Plus, Home, FileText } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatCpfCnpj, formatPhone, formatStatus } from '../utils/formatters';
import * as XLSX from 'xlsx';
import { gerarRelatorioSimples } from '../utils/simpleReport';
import { 
  applyIconOnlyStyle,
  getButtonStyle
} from '../utils/buttonStyles';
import {
  getSelectStyle,
  getInputStyle,
  formGroupStyle,
  labelStyle
} from '../utils/formStyles';
import {
  getCardStyle,
  getCardGridStyle,
  cardTitleStyle,
  cardSubtextStyle,
  cardInfoLineStyle,
  cardValueStyle,
  getCardTitleColor
} from '../utils/cardStyles';
import {
  listContainerStyle,
  loadingIndicatorStyle,
  spinnerStyle,
  emptyListStyle,
  emptyListIconStyle,
  pageHeaderStyle,
  obraCardStyle,
  obraCardHeaderStyle,
  obraCardTitleStyle,
  obraCardStatusStyle,
  obraCardBodyStyle,
  obraCardInfoLineStyle,
  obraCardLabelStyle,
  obraCardValueStyle,
  obraCardFooterStyle,
  getFiltersContainerStyle,
  searchFieldContainerStyle,
  searchIconStyle,
  paginationContainerStyle,
  getInfoBadgeStyle
} from '../utils/listStyles';

/**
 * Componente que exibe a lista de propriedades rurais cadastradas
 */
const ListaPropriedadesRurais = () => {
  const { 
    propriedades, 
    residencias,
    fossas,
    carregarPropriedades, 
    carregarResidencias,
    carregarFossas,
    loading, 
    filtros, 
    setFiltros,
    localidades,
    agentes,
    carregarLocalidades,
    carregarAgentes,
    estatisticas
  } = useFossasSepticas();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  // Carregar dados ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      await carregarLocalidades();
      await carregarAgentes();
      await carregarPropriedades();
      await carregarResidencias();
      await carregarFossas();
    };
    
    fetchData();
  }, [carregarPropriedades, carregarLocalidades, carregarAgentes, carregarResidencias, carregarFossas]);

  // Filtrar propriedades por termo de busca
  const propriedadesFiltradas = propriedades.filter(prop => {
    // Filtrar por termo de busca
    if (searchTerm && !prop.nome.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !prop.nome_proprietario.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Calcular propriedades para a página atual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPropriedades = propriedadesFiltradas.slice(indexOfFirstItem, indexOfLastItem);

  // Mudar de página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Aplicar filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
    setCurrentPage(1); // Voltar para a primeira página ao filtrar
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      status: null,
      localidade: null,
      agente: null,
      temFossa: null
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Exportar para Excel
  const exportarParaExcel = () => {
    const dadosExport = propriedadesFiltradas.map(prop => {
      return {
        'Nome da Propriedade': prop.nome,
        'Proprietário': prop.nome_proprietario,
        'CPF/CNPJ': prop.cpf_cnpj || '',
        'Telefone': prop.telefone,
        'Status': prop.status,
        'Localidade': prop.localidade?.nome || 'Não definida',
        'Agente': prop.agente?.nome || 'Não atribuído',
        'Latitude': prop.latitude,
        'Longitude': prop.longitude,
        'Data de Cadastro': new Date(prop.created_at).toLocaleDateString('pt-BR')
      };
    });

    const ws = XLSX.utils.json_to_sheet(dadosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Propriedades');
    XLSX.writeFile(wb, 'propriedades-rurais.xlsx');
  };
  
  // Gerar relatório em PDF
  const gerarRelatorioPDF = () => {
    // Obter filtros aplicados para incluir no relatório
    const filtrosAplicados = {};
    
    if (filtros.localidade) {
      const localidade = localidades.find(l => l.id === parseInt(filtros.localidade));
      if (localidade) {
        filtrosAplicados.localidade = localidade.descricao;
      }
    }
    
    if (filtros.status) {
      filtrosAplicados.status = filtros.status;
    }
    
    if (filtros.agente) {
      const agente = agentes.find(a => a.id === parseInt(filtros.agente));
      if (agente) {
        filtrosAplicados.agente = agente.nome;
      }
    }
    
    // Gerar o relatório com os dados filtrados
    gerarRelatorioSimples(propriedadesFiltradas, residencias, fossas, filtrosAplicados);
  };

  // Calcular totalizadores
  const calcularTotalizadores = () => {
    return {
      'Mapeada': {
        total: estatisticas.propriedades.mapeadas || 0,
        comFossa: estatisticas.residencias.comFossa || 0
      },
      'Não Mapeada': {
        total: estatisticas.propriedades.naoMapeadas || 0,
        semFossa: estatisticas.residencias.semFossa || 0
      },
      'Total': {
        total: estatisticas.propriedades.total || 0,
        residencias: estatisticas.residencias.total || 0
      }
    };
  };

  const totalizadores = calcularTotalizadores();

  return (
    <div>
      <div className="card">
        <div style={pageHeaderStyle}>
          <h2>Propriedades Rurais</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={exportarParaExcel}
              style={getButtonStyle('secondary')}
            >
              <Download size={16} />
              Exportar Excel
            </button>
            <button 
              onClick={gerarRelatorioPDF}
              style={getButtonStyle('info')}
            >
              <FileText size={16} />
              Relatório PDF
            </button>
            <Link 
              to="/fossas-septicas/cadastro" 
              style={getButtonStyle('primary')}
            >
              <Plus size={16} />
              Nova Propriedade
            </Link>
          </div>
        </div>

        {/* Cards de Totalizadores */}
        <div style={getCardGridStyle(3)}>
          {/* Card Mapeadas */}
          <div style={getCardStyle('success')}>
            <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('success') }}>Propriedades Mapeadas</h3>
            <div style={cardValueStyle}>
              {totalizadores['Mapeada'].total}
            </div>
            <div style={cardInfoLineStyle}>
              <span style={cardSubtextStyle}>Com Fossa Séptica:</span>
              <strong>{totalizadores['Mapeada'].comFossa}</strong>
            </div>
            <div style={cardInfoLineStyle}>
              <span style={cardSubtextStyle}>Porcentagem:</span>
              <strong>{totalizadores['Total'].total > 0 ? Math.round((totalizadores['Mapeada'].total / totalizadores['Total'].total) * 100) : 0}%</strong>
            </div>
          </div>
          
          {/* Card Não Mapeadas */}
          <div style={getCardStyle('danger')}>
            <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('danger') }}>Propriedades Não Mapeadas</h3>
            <div style={cardValueStyle}>
              {totalizadores['Não Mapeada'].total}
            </div>
            <div style={cardInfoLineStyle}>
              <span style={cardSubtextStyle}>Sem Fossa Séptica:</span>
              <strong>{totalizadores['Não Mapeada'].semFossa}</strong>
            </div>
            <div style={cardInfoLineStyle}>
              <span style={cardSubtextStyle}>Porcentagem:</span>
              <strong>{totalizadores['Total'].total > 0 ? Math.round((totalizadores['Não Mapeada'].total / totalizadores['Total'].total) * 100) : 0}%</strong>
            </div>
          </div>
          
          {/* Card Total */}
          <div style={getCardStyle('neutral')}>
            <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('neutral') }}>Total Geral</h3>
            <div style={cardValueStyle}>
              {totalizadores['Total'].total}
            </div>
            <div style={cardInfoLineStyle}>
              <span style={cardSubtextStyle}>Residências:</span>
              <strong>{totalizadores['Total'].residencias}</strong>
            </div>
            <div style={cardInfoLineStyle}>
              <span style={cardSubtextStyle}>Fossas Sépticas:</span>
              <strong>{totalizadores['Mapeada'].comFossa}</strong>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div style={getFiltersContainerStyle(4)}>
          <div style={{ ...searchFieldContainerStyle, ...formGroupStyle }}>
            <label style={labelStyle}>Buscar</label>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={searchIconStyle} />
              <input
                type="text"
                placeholder="Buscar por nome da propriedade ou proprietário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ 
                  ...getInputStyle(), 
                  paddingLeft: '40px'
                }}
              />
            </div>
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Status</label>
            <select
              name="status"
              value={filtros.status || ''}
              onChange={handleFiltroChange}
              style={getSelectStyle()}
            >
              <option value="">Todos os status</option>
              <option value="Mapeada">Mapeada</option>
              <option value="Não Mapeada">Não Mapeada</option>
              <option value="Em Análise">Em Análise</option>
            </select>
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Localidade</label>
            <select
              name="localidade"
              value={filtros.localidade || ''}
              onChange={handleFiltroChange}
              style={getSelectStyle()}
            >
              <option value="">Todas as localidades</option>
              {localidades.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.descricao}</option>
              ))}
            </select>
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Agente</label>
            <select
              name="agente"
              value={filtros.agente || ''}
              onChange={handleFiltroChange}
              style={getSelectStyle()}
            >
              <option value="">Todos os agentes</option>
              {agentes.filter(a => a.ativo).map(agente => (
                <option key={agente.id} value={agente.id}>{agente.nome}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
          <button 
            onClick={limparFiltros}
            style={getButtonStyle('secondary')}
          >
            Limpar Filtros
          </button>
        </div>

        {/* Lista de propriedades */}
        {loading.propriedades ? (
          <div style={loadingIndicatorStyle}>
            <div style={spinnerStyle}></div>
            <p>Carregando propriedades...</p>
          </div>
        ) : (
          <>
            {propriedadesFiltradas.length === 0 ? (
              <div style={emptyListStyle}>
                <Shield size={48} style={emptyListIconStyle} />
                <h3>Nenhuma propriedade encontrada</h3>
                <p>Não foram encontradas propriedades com os filtros aplicados.</p>
                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <button 
                    onClick={limparFiltros}
                    style={getButtonStyle('secondary')}
                  >
                    Limpar Filtros
                  </button>
                  <Link 
                    to="/fossas-septicas/cadastro" 
                    style={getButtonStyle('primary')}
                  >
                    <Plus size={16} />
                    Cadastrar Nova Propriedade
                  </Link>
                </div>
              </div>
            ) : (
              <div style={listContainerStyle}>
                {currentPropriedades.map(prop => {
                  const statusInfo = formatStatus(prop.status);
                  return (
                    <div key={prop.id} style={obraCardStyle}>
                      {/* Cabeçalho do card */}
                      <div style={obraCardHeaderStyle}>
                        <h3 style={obraCardTitleStyle}>{prop.nome}</h3>
                        <span style={{ 
                          ...obraCardStatusStyle, 
                          backgroundColor: statusInfo.color,
                          color: 'white'
                        }}>
                          {statusInfo.text}
                        </span>
                      </div>
                      
                      {/* Corpo do card */}
                      <div style={obraCardBodyStyle}>
                        {/* Contadores de residências e fossas em uma linha horizontal */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '0 10px 10px',
                          borderBottom: '1px solid #eee',
                          marginBottom: '10px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '5px',
                            backgroundColor: '#e8f5e9',
                            padding: '5px 12px',
                            borderRadius: '20px',
                            color: '#2e7d32'
                          }}>
                            <Home size={16} style={{ color: '#2e7d32' }} />
                            <span style={{ fontWeight: 'bold' }}>{prop.residencias?.length || 0}</span>
                            <span>Residências</span>
                          </div>
                          
                          <div style={{ 
                            flex: 1, 
                            height: '2px', 
                            background: 'linear-gradient(to right, #e8f5e9, #f3e5f5)',
                            margin: '0 15px'
                          }}></div>
                          
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '5px',
                            backgroundColor: '#f3e5f5',
                            padding: '5px 12px',
                            borderRadius: '20px',
                            color: '#0d47a1'
                          }}>
                            <span>Fossas Sépticas</span>
                            <span style={{ fontWeight: 'bold' }}>{prop.fossas?.length || 0}</span>
                            <Shield size={16} style={{ color: '#0d47a1' }} />
                          </div>
                        </div>
                        
                        {/* Informações em duas colunas de 3 itens cada */}
                        <div style={{ display: 'flex', gap: '20px' }}>
                          {/* Coluna da esquerda - 3 primeiros itens */}
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              ...obraCardInfoLineStyle, 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                ...obraCardLabelStyle, 
                                color: '#666', 
                                fontSize: '12px',
                                marginRight: '0'
                              }}>Proprietário:&nbsp;</span>
                              <span style={{ 
                                ...obraCardValueStyle, 
                                fontWeight: '500' 
                              }}>{prop.nome_proprietario}</span>
                            </div>
                            
                            <div style={{ 
                              ...obraCardInfoLineStyle, 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                ...obraCardLabelStyle, 
                                color: '#666', 
                                fontSize: '12px',
                                marginRight: '0'
                              }}>CPF/CNPJ:&nbsp;</span>
                              <span style={{ 
                                ...obraCardValueStyle, 
                                fontWeight: '500' 
                              }}>{prop.cpf_cnpj ? formatCpfCnpj(prop.cpf_cnpj) : 'Não informado'}</span>
                            </div>
                            
                            <div style={{ 
                              ...obraCardInfoLineStyle, 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                ...obraCardLabelStyle, 
                                color: '#666', 
                                fontSize: '12px',
                                marginRight: '0'
                              }}>Contato:&nbsp;</span>
                              <span style={{ 
                                ...obraCardValueStyle, 
                                fontWeight: '500' 
                              }}>{formatPhone(prop.telefone)}</span>
                            </div>
                          </div>
                          
                          {/* Coluna da direita - 3 últimos itens */}
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              ...obraCardInfoLineStyle, 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                ...obraCardLabelStyle, 
                                color: '#666', 
                                fontSize: '12px',
                                marginRight: '0'
                              }}>Localidade:&nbsp;</span>
                              <span style={{ 
                                ...obraCardValueStyle, 
                                fontWeight: '500' 
                              }}>{prop.localidade?.nome || 'Não definida'}</span>
                            </div>
                            
                            <div style={{ 
                              ...obraCardInfoLineStyle, 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                ...obraCardLabelStyle, 
                                color: '#666', 
                                fontSize: '12px',
                                marginRight: '0'
                              }}>Agente:&nbsp;</span>
                              <span style={{ 
                                ...obraCardValueStyle, 
                                fontWeight: '500' 
                              }}>{prop.agente?.nome || 'Não atribuído'}</span>
                            </div>
                            
                            <div style={{ 
                              ...obraCardInfoLineStyle, 
                              display: 'flex', 
                              alignItems: 'center',
                              marginBottom: '4px' 
                            }}>
                              <span style={{ 
                                ...obraCardLabelStyle, 
                                color: '#666', 
                                fontSize: '12px',
                                marginRight: '0'
                              }}>Coordenadas:&nbsp;</span>
                              <span style={{ 
                                ...obraCardValueStyle, 
                                fontWeight: '500' 
                              }}>{prop.latitude.toFixed(6)}, {prop.longitude.toFixed(6)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Rodapé do card com botões */}
                      <div style={obraCardFooterStyle}>
                        <Link 
                          to={`/fossas-septicas/editar/${prop.id}`} 
                          style={getButtonStyle('secondary', { small: true })}
                        >
                          <Edit size={14} />
                          Editar
                        </Link>
                        <Link 
                          to={`/fossas-septicas/residencias/${prop.id}`} 
                          style={getButtonStyle('info', { small: true })}
                        >
                          <Eye size={14} />
                          Residências
                        </Link>
                        <Link 
                          to={`/fossas-septicas/visitas/${prop.id}`} 
                          style={getButtonStyle('success', { small: true })}
                        >
                          <Calendar size={14} />
                          Visitas
                        </Link>
                        <Link 
                          to={`/fossas-septicas/mapa?propriedade=${prop.id}`} 
                          style={getButtonStyle('neutral', { small: true })}
                        >
                          <MapPin size={14} />
                          Ver no Mapa
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Paginação */}
            {propriedadesFiltradas.length > itemsPerPage && (
              <div style={paginationContainerStyle}>
                {Array.from({ length: Math.ceil(propriedadesFiltradas.length / itemsPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    style={{
                      ...applyIconOnlyStyle(
                        getButtonStyle(currentPage === index + 1 ? 'primary' : 'secondary')
                      ),
                      width: '32px',
                      height: '32px',
                      margin: '0 5px',
                      padding: '0',
                      justifyContent: 'center'
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ListaPropriedadesRurais;